import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries, useUpdateDossierPropertyQueriesData } from "@/entities/dossier";
import { useApiClient } from "@/infrastructure/rest";
import { LandelijkeFaciliteitenEvent, useRealtimeTopic, VestigingEvent } from "@/infrastructure/rsocket";
import {
    AangevraagdeVestigingDto,
    LandelijkeFaciliteitenAanvraagDto,
    UpsertVestigingDto,
    VestigingAanvraagDto,
    VestigingDto,
} from "@/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AANGEVRAADGE_LANDELIJKE_FACILITEITEN_QUERY_KEY, VESTIGINGEN_QUERY_KEY } from "./constants";
import { AangevraagdeLandelijkeFaciliteiten, AangevraagdeVestiging, BeheerVestiging } from "./domain";

export function useGetAangevraagdeVestigingen(dossierId: string) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, VESTIGINGEN_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<AangevraagdeVestigingDto[]>(`/dossiers/${dossierId}/vestigingen`);
            return data?.map((v) => AangevraagdeVestiging.fromDto(v)) ?? [];
        },
    });
}

export function useGetAangevraagdeLandelijkeFaciliteiten(dossierId: string) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, AANGEVRAADGE_LANDELIJKE_FACILITEITEN_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<LandelijkeFaciliteitenAanvraagDto>(`/dossiers/${dossierId}/landelijke-faciliteiten`);
            return data ? AangevraagdeLandelijkeFaciliteiten.fromDto(data) : null;
        },
    });
}

export function useRealtimeVestigingen() {
    const { handleEvent: handleVestigingEvent } = useUpdateDossierPropertyQueriesData("vestigingen");

    useRealtimeTopic<VestigingEvent>("vestigingen", {
        onMessage: (message) => {
            const { aangevraagdeVestiging, dossierId, reason } = message;
            const vestiging = AangevraagdeVestiging.fromDto(aangevraagdeVestiging);
            // create, update or delete the vestiging from the ["dossiers", dossierId, "vestigingen"] query cache
            handleVestigingEvent(dossierId, vestiging.id, reason, vestiging);
        },
    });
}

export function useRealtimeLandelijkeFaciliteiten() {
    const queryClient = useQueryClient();
    // Use a lightweight event instead of a payload-rich one for simplicity
    useRealtimeTopic<LandelijkeFaciliteitenEvent>("landelijkeFaciliteiten", {
        onMessage: (message) => {
            void queryClient.invalidateQueries({
                queryKey: [DOSSIERS_QUERY_KEY, String(message.dossierId), AANGEVRAADGE_LANDELIJKE_FACILITEITEN_QUERY_KEY],
            });
        },
    });
}

type PostDossierVestigingen = {
    dossierId: string;
    vestigingen: VestigingAanvraagDto[];
};

export function usePostDossierVestigingen() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, vestigingen }: PostDossierVestigingen) => {
            await apiClient.post(`/dossiers/${dossierId}/vestigingen`, vestigingen);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId.toString());
        },
        onError: (error) => {
            console.error("Error posting vestigingen:", error);
        },
    });
}

type PatchDossierVestigingen = {
    dossierId: string;
    vestigingen: AangevraagdeVestigingDto[];
};

export function usePatchDossierVestigingen() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, vestigingen }: PatchDossierVestigingen) => {
            await apiClient.patch(`/dossiers/${dossierId}/vestigingen`, vestigingen);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId.toString());
        },
        onError: (error) => {
            console.error("Error patching vestigingen:", error);
        },
    });
}

type PostLandelijkeFaciliteiten = {
    dossierId: string;
};

export function usePostLandelijkeFaciliteiten() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId }: PostLandelijkeFaciliteiten) => {
            await apiClient.post(`/dossiers/${dossierId}/landelijke-faciliteiten`);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId.toString());
        },
        onError: (error) => {
            console.error("Error patching landelijke faciliteiten:", error);
        },
    });
}

type PatchLandelijkeFaciliteiten = LandelijkeFaciliteitenAanvraagDto & {
    dossierId: string;
};

export function usePatchLandelijkeFaciliteiten() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, ...body }: PatchLandelijkeFaciliteiten) => {
            await apiClient.patch(`/dossiers/${dossierId}/landelijke-faciliteiten`, body);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId.toString());
        },
        onError: (error) => {
            console.error("Error patching landelijke faciliteiten:", error);
        },
    });
}

export function useCreateVestiging() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();
    return useMutation({
        mutationFn: async (payload: UpsertVestigingDto) => {
            return await apiClient.post<VestigingDto>("/vestigingen", payload);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [VESTIGINGEN_QUERY_KEY] });
        },
        onError: (error) => {
            console.error("Error creating vestiging:", error);
        },
    });
}

export function useGetBeheerVestigingen() {
    const apiClient = useApiClient();
    return useQuery({
        queryKey: [VESTIGINGEN_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<VestigingDto[]>("/infra/vestigingen");
            return data?.map((v) => BeheerVestiging.fromDto(v)) ?? [];
        },
    });
}

export function useGetBeheerVestigingenByStations(stationCodes: string[]) {
    const apiClient = useApiClient();
    const sortedCodes = [...stationCodes].sort((a, b) => a.localeCompare(b));
    return useQuery({
        queryKey: [VESTIGINGEN_QUERY_KEY, sortedCodes],
        queryFn: async () => {
            const query = `?stations=${sortedCodes.join(",")}`;
            const data = await apiClient.get<VestigingDto[]>(`/infra/vestigingen${query}`);
            return data?.map((v) => BeheerVestiging.fromDto(v)) ?? [];
        },
    });
}

export function useGetLazyBeheerVestigingenByStations() {
    const apiClient = useApiClient();

    return async (stationCodes: string[]) => {
        const query = `?stations=${stationCodes.join(",")}`;
        const data = await apiClient.get<VestigingDto[]>(`/infra/vestigingen${query}`);
        return data?.map((v) => BeheerVestiging.fromDto(v)) ?? [];
    };
}

export function useUpdateVestiging() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: UpsertVestigingDto }) => {
            return await apiClient.put<VestigingDto>(`/vestigingen/${id}`, payload);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [VESTIGINGEN_QUERY_KEY] });
        },
        onError: (error) => {
            console.error("Error updating vestiging:", error);
        },
    });
}

export function useDeleteVestiging() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();
    return useMutation({
        mutationFn: async (id: number) => {
            await apiClient.delete(`/vestigingen/${id}`);
            return id;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [VESTIGINGEN_QUERY_KEY] });
        },
        onError: (error) => {
            console.error("Error deleting vestiging:", error);
        },
    });
}

export function useSyncVestigingen() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();
    return useMutation({
        mutationFn: async () => {
            await apiClient.post<void>("/vestigingen/sync", {});
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [VESTIGINGEN_QUERY_KEY] });
        },
        onError: (error) => {
            console.error("Error syncing vestigingen:", error);
        },
    });
}
